import { useSearchModifiers } from 'controllers/SearchController/hooks/useSearchModifiers';
import { useCallback } from 'react';
import { FiltersParams, TagCategoryParams } from 'types/params';
import { useCurrentSearchState } from 'controllers/SearchController/hooks/useCurrentSearchState';
import { useFiltersContext } from 'components/Filters/context/FiltersContext/FiltersContext';
import { t } from '@lingui/macro';
import { searchV3FilterType } from 'components/Filters/Filters';
import { useSearchController } from 'controllers/SearchController/SearchContext';
import useHasFilterApplied from 'components/Filters/context/SearchBarContext/hooks/useHasFilterApplied';
import { usePaneResults } from 'components/DetailsView/components/PaneResults';
import { useBpmAndKeys } from './useBpmAndKeys';

const useSearchTools = ({
    toolsType,
}: {
    toolsType: searchV3FilterType | FiltersParams | TagCategoryParams;
}) => {
    const { clearByFilter } = useSearchModifiers();
    const { bpm } = useBpmAndKeys();
    const { keyrootnote, keyquality } = useBpmAndKeys();
    const { type: typeState } = useCurrentSearchState();
    const { listOfTypes } = useFiltersContext();

    const { samplesTotal } = useSearchController();
    const hasFilterApplied = useHasFilterApplied({ includeType: true });
    const { resultsCopy } = usePaneResults({
        total: samplesTotal,
        showNoFilterCopy: false,
        hasFilterApplied,
    });

    const isTypeFilter = toolsType === searchV3FilterType.Types;
    const isBpmFilter = toolsType === searchV3FilterType.BPM;
    const isKeyFilter = toolsType === searchV3FilterType.Key;

    let isBpmActive = false;
    let isKeyActive = false;
    let isTypeActive = false;
    let ActiveTypeLabel = t`Loop & One-shot`;

    if ((isBpmFilter && bpm[0] !== null) || bpm[1] !== null) {
        isBpmActive = true;
    }

    if ((isKeyFilter && keyrootnote) || keyquality) {
        isKeyActive = true;
    }

    if (isTypeFilter && typeState) {
        isTypeActive = true;
        if (typeState === listOfTypes?.list?.[0]?.code) {
            ActiveTypeLabel = t`Loop`;
        } else if (typeState === listOfTypes?.list?.[1]?.code) {
            ActiveTypeLabel = t`One-shot`;
        }
    }

    const clearTypeFilter = useCallback(() => {
        clearByFilter(FiltersParams.type);
    }, [clearByFilter]);

    const clearBpmFilter = useCallback(() => {
        clearByFilter(FiltersParams.bpmlowerbound);
        clearByFilter(FiltersParams.bpmupperbound);
    }, [clearByFilter]);

    const clearKeyFilter = useCallback(() => {
        clearByFilter(FiltersParams.keyrootnote);
        clearByFilter(FiltersParams.keyquality);
    }, [clearByFilter]);

    const clearFilter = useCallback(() => {
        isTypeFilter && clearTypeFilter();
        isBpmFilter && clearBpmFilter();
        isKeyFilter && clearKeyFilter();
    }, [
        clearTypeFilter,
        clearBpmFilter,
        clearKeyFilter,
        isTypeFilter,
        isBpmFilter,
        isKeyFilter,
    ]);

    return {
        clearFilter,
        isBpmActive,
        isKeyActive,
        isTypeActive,
        ActiveTypeLabel,
        isTypeFilter,
        isBpmFilter,
        isKeyFilter,
        resultsCopy,
    };
};

export default useSearchTools;
